import { BaseCampaignItem } from '@retail/campaign/types';
import { useMemo } from 'react';
import { Filter, filterList } from '@shared/utils';
import { useFilters } from '@retail/products/hooks';
import { useFetchAllSuppliers, useFetchBk04ProductGroups } from '@retail/products/data-access';
import { SupplierBase } from '@retail/suppliers/types';
import { ProductGroupCount } from '@retail/products/types';

interface Props<T extends BaseCampaignItem> {
  campaignItems?: T[];
}

export const useCampaignFiltering = <T extends BaseCampaignItem>({
  campaignItems = [],
}: Props<T>) => {
  const { filters, setFilters, resetFilters } = useFilters([
    'searchQuery',
    'supplierNumber',
    'productGroupNumber',
  ] as const);

  const { data: suppliersDtos } = useFetchAllSuppliers();
  const allSuppliers = useMemo(
    () => suppliersDtos?.map(SupplierBase.fromSupplierDTO) || [],
    [suppliersDtos]
  );
  // Get suppliers for a specific campaign
  const campaignSuppliers = useMemo(() => {
    const uniqueSupplierIds = Array.from(new Set(campaignItems?.map((item) => item.mgSupplierId)));
    const updatedSupplierIds = uniqueSupplierIds.map(
      (supplierId) => allSuppliers.find((s) => s.supplierNumber === supplierId)!
    );

    return updatedSupplierIds;
  }, [allSuppliers, campaignItems]);

  const { data: productGroupsDto = [] } = useFetchBk04ProductGroups({
    suspense: true,
  });
  const productGroups = productGroupsDto.map(ProductGroupCount.fromDto);
  const flattenedProductGroups = ProductGroupCount.flatten(productGroups);

  // Get product groups for a specific campaign
  const campaignProductGroups = useMemo(() => {
    const uniqueGroupNumbers = Array.from(new Set(campaignItems?.map((item) => item.groupNumber)));
    const updatedGroupNumbers = uniqueGroupNumbers.map(
      (groupNumber) => flattenedProductGroups.find((group) => group.groupNumber === groupNumber)!
    );

    return updatedGroupNumbers;
  }, [campaignItems, flattenedProductGroups]);

  const filteredCampaignItems = useMemo(() => {
    const searchLower = (filters.searchQuery ?? '').trim().toLowerCase();
    const hasSearch = searchLower.length > 0;
    const internalFilters: Filter<T>[] = [
      {
        active: hasSearch,
        predicate: (item) =>
          item.primaryText?.toLowerCase().includes(searchLower) ||
          item.secondaryText?.toLowerCase().includes(searchLower) ||
          String(item.mgItemNumber).includes(searchLower),
      },
      {
        active: !!filters.supplierNumber,
        predicate: (item) => item.mgSupplierId === Number(filters.supplierNumber),
      },
      {
        active: !!filters.productGroupNumber,
        predicate: (item) => item.groupNumber === filters.productGroupNumber,
      },
    ];

    return filterList({ filters: internalFilters, initList: campaignItems });
  }, [campaignItems, filters.productGroupNumber, filters.searchQuery, filters.supplierNumber]);

  return {
    filteredCampaignItems,
    suppliers: campaignSuppliers,
    productGroups: campaignProductGroups,
    filters,
    setSearchQuery: (query: string | null) => setFilters({ searchQuery: query }),
    setSupplierNumber: (supplierNumber: number | null) =>
      setFilters({ supplierNumber: supplierNumber ? supplierNumber.toString() : undefined }),
    setProductGroupNumber: (groupNumber: string | null) =>
      setFilters({ productGroupNumber: groupNumber }),
    resetFilters,
  };
};
